import React, { FC } from 'react';
import type { MouseEventHandler } from 'preact';
import { cls } from '../../View/utils';
import styles from './Button.module.scss';
import { DataAttributes } from '../../appTypings';
import { Pointer } from '@yleisradio/areena-types';
import ReactRouterLink from '../../Routes/ReactRouterLink';

type ButtonSize = 'md' | 'sm';
export type ButtonType = 'text' | 'primary' | 'secondary';

type Props = {
  type: ButtonType;
  text: string;
  size?: ButtonSize;
  fullWidth?: boolean;
  id: string;
  onClick: MouseEventHandler<HTMLElement> | undefined;
  disabled?: boolean;
  dataTestId?: string;
  mode?: 'light' | 'dark';
  onFocus?: () => void;
  icon?: React.JSX.Element;
  iconPosition?: 'left' | 'right';
  impressionAttributes?: DataAttributes;
  dataEngagementAttributes?: DataAttributes;
  dataYleContext?: DataAttributes;
  pointer?: Pointer | undefined;
} & React.AriaAttributes;

type IconProps = {
  icon?: React.JSX.Element;
  iconPosition?: 'left' | 'right';
};

const Icon: FC<IconProps> = ({ icon, iconPosition }) =>
  icon && iconPosition ? (
    <span
      className={cls({
        [styles.icon]: true,
        [styles[`icon-${iconPosition}`]]: true,
      })}
    >
      {icon}
    </span>
  ) : null;

const ButtonContent: FC<Omit<Props, 'id' | 'onClick'>> = ({
  text,
  type = 'primary',
  size,
  fullWidth,
  disabled,
  icon,
  iconPosition,
  mode = 'light',
}) => (
  <div
    className={cls({
      [styles.focusBorder]: true,
      [styles.fullWidth]: fullWidth,
    })}
  >
    <div
      className={cls({
        [styles.innerContainer]: true,
        [styles[`type-${type}-${mode}`]]: !disabled,
        [styles[`size-${size}`]]: true,
        [styles[`type-${type}-disabled`]]: disabled,
        [styles.fullWidth]: fullWidth,
      })}
    >
      {iconPosition === 'left' && (
        <Icon icon={icon} iconPosition={iconPosition} />
      )}
      {text}
      {iconPosition === 'right' && (
        <Icon icon={icon} iconPosition={iconPosition} />
      )}
    </div>
  </div>
);

/**
 * Design system Button https://www.figma.com/design/EUPz3rtLKx4YNCWFWRZfAq/Areena-Smart-DESIGN-720p?node-id=4926-129191&m=dev
 */
const Button: FC<Props> = ({
  text,
  type,
  size = 'md',
  fullWidth = false,
  id,
  onClick,
  disabled,
  dataTestId,
  icon,
  iconPosition,
  impressionAttributes,
  dataEngagementAttributes,
  dataYleContext,
  pointer,
  mode = 'light',
  onFocus,
  ...props
}) => {
  const buttonClasses = cls({
    [styles[`button-${mode}`]]: true,
    [styles.disabled]: disabled,
    [styles.fullWidth]: fullWidth,
  });

  return pointer && !disabled ? (
    <ReactRouterLink
      id={id}
      className={buttonClasses}
      onClick={onClick}
      onFocus={onFocus}
      pointer={pointer}
      aria-label={props['aria-label'] || text}
      impressionAttributes={impressionAttributes}
      dataEngagementAttributes={dataEngagementAttributes}
      dataTestId={dataTestId}
    >
      <ButtonContent
        text={text}
        type={type}
        size={size}
        fullWidth={fullWidth}
        icon={icon}
        iconPosition={iconPosition}
        mode={mode}
      />
    </ReactRouterLink>
  ) : (
    <button
      className={buttonClasses}
      onClick={onClick}
      onFocus={onFocus}
      id={id}
      data-testid={dataTestId}
      type={'button'}
      aria-label={props['aria-label'] || text}
      aria-disabled={disabled && 'true'}
      {...impressionAttributes}
      {...dataEngagementAttributes}
    >
      <ButtonContent
        text={text}
        type={type}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        icon={icon}
        iconPosition={iconPosition}
        mode={mode}
      />
    </button>
  );
};

export default Button;
